// activity-log-detail-modal.component.ts
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { CustomDatePipe } from 'src/app/pipe/date-format.pipe';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-activity-log-detail-modal',
  standalone: true,
  imports: [CommonModule, CustomDatePipe],
  template: `
    <div class="modal-container">
      <div class="modal-header">
        <div class="header-content">
          <div class="header-icon" [ngClass]="getEventConfig(data.event_type).colorClass">
            <span class="material-icons">{{ getEventConfig(data.event_type).icon }}</span>
          </div>
          <div class="header-text">
            <h2>{{ getEventConfig(data.event_type).label }}</h2>
            <span class="header-time">
              <span class="material-icons">schedule</span>
              {{ data.created_at | customDate }}
            </span>
          </div>
        </div>
        <button class="close-btn" (click)="close()">
          <span class="material-icons">close</span>
        </button>
      </div>

      <div class="modal-body">
        <!-- Activity Detail -->
        @if (data.activity) {
          <div class="detail-card">
            <div class="card-header">
              <span class="material-icons">task_alt</span>
              <span>Activity Details</span>
            </div>
            <div class="card-body">
              <div class="detail-grid">
                <div class="detail-item">
                  <span class="label">Title</span>
                  <span class="value">{{ data.activity.title || '-' }}</span>
                </div>
                <div class="detail-item">
                  <span class="label">Status</span>
                  <span class="value status-badge" [class.completed]="data.activity.completed">
                    {{ data.activity.status || '-' }}
                  </span>
                </div>
                @if (data.activity.assignedTo) {
                  <div class="detail-item">
                    <span class="label">Assigned To</span>
                    <span class="value">{{ data.activity.assignedTo.name }} {{ data.activity.assignedTo.lastName }}</span>
                  </div>
                }
                @if (data.activity.dueDate) {
                  <div class="detail-item">
                    <span class="label">Due Date</span>
                    <span class="value">{{ data.activity.dueDate | customDate }}</span>
                  </div>
                }
                @if (data.activity.type) {
                  <div class="detail-item full-width">
                    <span class="label">Type</span>
                    <span class="value">{{ data.activity.type.name }}</span>
                  </div>
                }
              </div>
            </div>
          </div>
        }

        <!-- Message Detail -->
        @if (data.message) {
          <div class="detail-card">
            <div class="card-header">
              <span class="material-icons">{{ data.message.isSms ? 'sms' : 'mail_outline' }}</span>
              <span>{{ data.message.isSms ? 'SMS' : 'Email' }} Details</span>
            </div>
            <div class="card-body">
              <div class="detail-grid">
                @if (data.message.subject) {
                  <div class="detail-item full-width">
                    <span class="label">Subject</span>
                    <span class="value">{{ data.message.subject }}</span>
                  </div>
                }
                <div class="detail-item">
                  <span class="label">Direction</span>
                  <span class="value direction-badge" [class.inbound]="isReceived(data.message.direction)">
                    {{ isReceived(data.message.direction) ? 'Received' : 'Sent' }}
                  </span>
                </div>
                <div class="detail-item">
                  <span class="label">Status</span>
                  <span class="value">{{ getMessageStatus(data.message) }}</span>
                </div>
              </div>
              @if (data.message.content) {
                <div class="content-section">
                  <span class="label">Content</span>
                  <div class="content-box" [innerHTML]="data.message.content"></div>
                </div>
              }
            </div>
          </div>
        }

        <!-- Note Detail -->
        @if (data.note) {
          <div class="detail-card">
            <div class="card-header">
              <span class="material-icons">sticky_note_2</span>
              <span>Note</span>
              @if (data.note.pined) {
                <span class="pinned-badge">
                  <span class="material-icons">push_pin</span>
                  Pinned
                </span>
              }
            </div>
            <div class="card-body">
              <div class="content-box" [innerHTML]="data.note.content"></div>
            </div>
          </div>
        }

        <!-- Step Detail -->
        @if (data.step) {
          <div class="detail-card">
            <div class="card-header">
              <span class="material-icons">swap_horiz</span>
              <span>Step Change</span>
            </div>
            <div class="card-body">
              <div class="step-change">
                <div class="step-badge">
                  <span class="material-icons">flag</span>
                  {{ data.step.name }}
                </div>
                @if (data.step.pipeline) {
                  <span class="pipeline-name">in {{ data.step.pipeline.name }}</span>
                }
              </div>
              @if (data.step.step_type) {
                <div class="detail-item" style="margin-top: 12px;">
                  <span class="label">Step Type</span>
                  <span class="value">{{ data.step.step_type }}</span>
                </div>
              }
            </div>
          </div>
        }

        <!-- Appointment Detail -->
        @if (data.appointment) {
          <div class="detail-card">
            <div class="card-header">
              <span class="material-icons">event</span>
              <span>Appointment Details</span>
            </div>
            <div class="card-body">
              <div class="detail-grid">
                <div class="detail-item">
                  <span class="label">Status</span>
                  <span class="value">{{ data.appointment.status?.name || '-' }}</span>
                </div>
                <div class="detail-item">
                  <span class="label">Type</span>
                  <span class="value">{{ data.appointment.isVirtual ? 'Virtual' : 'In-Person' }}</span>
                </div>
                <div class="detail-item">
                  <span class="label">Start Time</span>
                  <span class="value">{{ data.appointment.scheduled_start | customDate }}</span>
                </div>
                <div class="detail-item">
                  <span class="label">End Time</span>
                  <span class="value">{{ data.appointment.scheduled_end | customDate }}</span>
                </div>
                @if (data.appointment.category) {
                  <div class="detail-item full-width">
                    <span class="label">Category</span>
                    <span class="value">{{ data.appointment.category.name }}</span>
                  </div>
                }
              </div>
            </div>
          </div>
        }

        <!-- Call Detail -->
        @if (data.call) {
          <div class="detail-card">
            <div class="card-header">
              <span class="material-icons">phone</span>
              <span>Call Details</span>
            </div>
            <div class="card-body">
              <div class="detail-grid">
                <div class="detail-item">
                  <span class="label">Status</span>
                  <span class="value">{{ data.call.call_status || '-' }}</span>
                </div>
                <div class="detail-item">
                  <span class="label">Direction</span>
                  <span class="value direction-badge" [class.inbound]="isReceived(data.call.direction)">
                    {{ isReceived(data.call.direction) ? 'Received' : 'Sent' }}
                  </span>
                </div>
                @if (data.call.duration) {
                  <div class="detail-item">
                    <span class="label">Duration</span>
                    <span class="value">{{ formatDuration(data.call.duration) }}</span>
                  </div>
                }
                @if (data.call.start_time) {
                  <div class="detail-item">
                    <span class="label">Start Time</span>
                    <span class="value">{{ data.call.start_time | customDate }}</span>
                  </div>
                }
              </div>
              @if (data.call?.recording?.s3_key) {
                <div class="audio-section">
                  <span class="label">Recording</span>
                  @if (loadingRecording) {
                    <div class="loading-audio">Loading recording...</div>
                  } @else if (recordingUrl) {
                    <audio controls [src]="recordingUrl"></audio>
                  } @else {
                    <div class="error-audio">Failed to load recording</div>
                  }
                </div>
              }
            </div>
          </div>
        }

        <!-- Metadata (if no specific data) -->
        @if (!data.activity && !data.message && !data.note && !data.step && !data.appointment && !data.call && data.metadata && hasMetadata(data.metadata)) {
          <div class="detail-card">
            <div class="card-header">
              <span class="material-icons">info_outline</span>
              <span>Event Details</span>
            </div>
            <div class="card-body">
              <div class="detail-grid">
                @for (item of getMetadataItems(data.metadata); track item.key) {
                  <div class="detail-item" [class.full-width]="item.fullWidth">
                    <span class="label">{{ item.label }}</span>
                    <span class="value">{{ item.value }}</span>
                  </div>
                }
              </div>
            </div>
          </div>
        }

        <!-- No details -->
        @if (!data.activity && !data.message && !data.note && !data.step && !data.appointment && !data.call && (!data.metadata || !hasMetadata(data.metadata))) {
          <div class="no-details">
            <span class="material-icons">info_outline</span>
            <p>No additional details available for this event.</p>
          </div>
        }
      </div>
    </div>
  `,
  styles: [`
    .modal-container {
      max-height: 85vh;
      display: flex;
      flex-direction: column;
      background: var(--lead-bg-primary, #ffffff);
      border-radius: 12px;
      overflow: hidden;
    }

    .modal-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      padding: 20px 24px;
      border-bottom: 1px solid var(--lead-border-color, #e2f5fa);
      background-color: var(--lead-bg-secondary, #f0f9fd);
    }

    .header-content {
      display: flex;
      align-items: center;
      gap: 14px;
    }

    .header-icon {
      width: 48px;
      height: 48px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      border: 2px solid;

      .material-icons {
        font-size: 24px;
      }

      &.color-activity { background-color: #E3F2FD; border-color: #90CAF9; color: #1976D2; }
      &.color-activity-done { background-color: #E8F5E9; border-color: #A5D6A7; color: #388E3C; }
      &.color-message-in { background-color: #F3E5F5; border-color: #CE93D8; color: #7B1FA2; }
      &.color-message-out { background-color: #E8EAF6; border-color: #9FA8DA; color: #3949AB; }
      &.color-note { background-color: #FFF8E1; border-color: #FFE082; color: #F57C00; }
      &.color-step { background-color: #E0F7FA; border-color: #80DEEA; color: #00838F; }
      &.color-appointment { background-color: #E0F2F1; border-color: #80CBC4; color: #00796B; }
      &.color-appointment-done { background-color: #E8F5E9; border-color: #A5D6A7; color: #388E3C; }
      &.color-call { background-color: #E8F5E9; border-color: #A5D6A7; color: #388E3C; }
      &.color-lead { background-color: #ECEFF1; border-color: #B0BEC5; color: #546E7A; }
      &.color-success { background-color: #E8F5E9; border-color: #A5D6A7; color: #2E7D32; }
      &.color-danger { background-color: #FFEBEE; border-color: #EF9A9A; color: #C62828; }
      &.color-default { background-color: var(--lead-bg-secondary, #f0f9fd); border-color: var(--lead-border-color, #e2f5fa); color: var(--lead-text-muted, #64748b); }
    }

    .header-text {
      h2 {
        margin: 0 0 4px 0;
        font-size: 17px;
        font-weight: 600;
        color: var(--lead-text-primary, #2c3e50);
      }
    }

    .header-time {
      display: flex;
      align-items: center;
      gap: 4px;
      font-size: 12px;
      color: var(--lead-text-muted, #64748b);

      .material-icons {
        font-size: 14px;
      }
    }

    .close-btn {
      background: none;
      border: none;
      cursor: pointer;
      padding: 6px;
      border-radius: 6px;
      color: var(--lead-text-muted, #64748b);
      transition: all 0.2s;

      &:hover {
        background-color: var(--lead-hover-bg, rgba(0, 161, 255, 0.08));
        color: var(--lead-text-primary, #2c3e50);
      }

      .material-icons {
        font-size: 20px;
      }
    }

    .modal-body {
      padding: 20px 24px;
      overflow-y: auto;
      flex: 1;
    }

    .detail-card {
      background-color: var(--lead-bg-tertiary, #ffffff);
      border: 1px solid var(--lead-border-color, #e2f5fa);
      border-radius: 10px;
      margin-bottom: 16px;
      overflow: hidden;

      &:last-child {
        margin-bottom: 0;
      }
    }

    .card-header {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 12px 16px;
      background-color: var(--lead-bg-secondary, #f0f9fd);
      border-bottom: 1px solid var(--lead-border-color, #e2f5fa);
      font-size: 13px;
      font-weight: 600;
      color: var(--lead-text-primary, #2c3e50);

      .material-icons {
        font-size: 18px;
        color: var(--lead-text-muted, #64748b);
      }
    }

    .pinned-badge {
      margin-left: auto;
      display: flex;
      align-items: center;
      gap: 4px;
      font-size: 11px;
      font-weight: 500;
      color: #F57C00;

      .material-icons {
        font-size: 14px;
        color: #F57C00;
      }
    }

    .card-body {
      padding: 16px;
    }

    .detail-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 16px;
    }

    .detail-item {
      display: flex;
      flex-direction: column;
      gap: 4px;

      &.full-width {
        grid-column: span 2;
      }

      .label {
        font-size: 11px;
        font-weight: 500;
        color: var(--lead-text-muted, #64748b);
        text-transform: uppercase;
        letter-spacing: 0.3px;
      }

      .value {
        font-size: 13px;
        color: var(--lead-text-primary, #2c3e50);
      }
    }

    .status-badge {
      display: inline-block;
      padding: 2px 8px;
      border-radius: 4px;
      background-color: var(--lead-bg-secondary, #f0f9fd);
      font-size: 12px;

      &.completed {
        background-color: #E8F5E9;
        color: #388E3C;
      }
    }

    .direction-badge {
      display: inline-block;
      padding: 2px 8px;
      border-radius: 4px;
      background-color: #E8EAF6;
      color: #3949AB;
      font-size: 12px;

      &.inbound {
        background-color: #F3E5F5;
        color: #7B1FA2;
      }
    }

    .step-change {
      display: flex;
      align-items: center;
      gap: 10px;
    }

    .step-badge {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 8px 12px;
      background-color: #E0F7FA;
      border-radius: 6px;
      font-size: 13px;
      font-weight: 500;
      color: #00838F;

      .material-icons {
        font-size: 16px;
      }
    }

    .pipeline-name {
      font-size: 12px;
      color: var(--lead-text-muted, #64748b);
    }

    .content-section {
      margin-top: 16px;

      .label {
        display: block;
        font-size: 11px;
        font-weight: 500;
        color: var(--lead-text-muted, #64748b);
        text-transform: uppercase;
        letter-spacing: 0.3px;
        margin-bottom: 8px;
      }
    }

    .content-box {
      padding: 14px;
      background-color: var(--lead-bg-primary, #ffffff);
      border: 1px solid var(--lead-border-color, #e2f5fa);
      border-radius: 8px;
      font-size: 13px;
      line-height: 1.6;
      color: var(--lead-text-primary, #2c3e50);
      max-height: 180px;
      overflow-y: auto;
    }

    .audio-section {
      margin-top: 16px;

      .label {
        display: block;
        font-size: 11px;
        font-weight: 500;
        color: var(--lead-text-muted, #64748b);
        text-transform: uppercase;
        letter-spacing: 0.3px;
        margin-bottom: 8px;
      }

      audio {
        width: 100%;
        height: 40px;
        border-radius: 8px;
      }

      .loading-audio, .error-audio {
        padding: 12px;
        font-size: 12px;
        color: var(--lead-text-muted, #64748b);
        background: var(--lead-bg-secondary, #f0f9fd);
        border-radius: 8px;
        text-align: center;
      }

      .error-audio {
        color: #C62828;
        background: #FFEBEE;
      }
    }

    .no-details {
      display: flex;
      flex-direction: column;
      align-items: center;
      padding: 40px 20px;
      color: var(--lead-text-muted, #64748b);
      text-align: center;

      .material-icons {
        font-size: 40px;
        margin-bottom: 12px;
        opacity: 0.5;
      }

      p {
        margin: 0;
        font-size: 13px;
      }
    }

    // =======================================================================
    // DARK THEME
    // =======================================================================
    :host-context(body.dark-theme) {
      .modal-container {
        background: #1a1a1a;
      }

      .modal-header {
        background-color: #2d2d2d;
        border-bottom-color: rgba(255, 255, 255, 0.08);
      }

      .header-text h2 {
        color: #f5f5f5;
      }

      .header-time {
        color: #808080;
      }

      .close-btn {
        color: #808080;

        &:hover {
          background-color: rgba(255, 255, 255, 0.08);
          color: #f5f5f5;
        }
      }

      .header-icon {
        &.color-activity { background-color: rgba(25, 118, 210, 0.15); border-color: rgba(25, 118, 210, 0.4); color: #64B5F6; }
        &.color-activity-done { background-color: rgba(56, 142, 60, 0.15); border-color: rgba(56, 142, 60, 0.4); color: #81C784; }
        &.color-message-in { background-color: rgba(123, 31, 162, 0.15); border-color: rgba(123, 31, 162, 0.4); color: #CE93D8; }
        &.color-message-out { background-color: rgba(57, 73, 171, 0.15); border-color: rgba(57, 73, 171, 0.4); color: #9FA8DA; }
        &.color-note { background-color: rgba(245, 124, 0, 0.15); border-color: rgba(245, 124, 0, 0.4); color: #FFB74D; }
        &.color-step { background-color: rgba(0, 131, 143, 0.15); border-color: rgba(0, 131, 143, 0.4); color: #4DD0E1; }
        &.color-appointment { background-color: rgba(0, 121, 107, 0.15); border-color: rgba(0, 121, 107, 0.4); color: #4DB6AC; }
        &.color-appointment-done { background-color: rgba(56, 142, 60, 0.15); border-color: rgba(56, 142, 60, 0.4); color: #81C784; }
        &.color-call { background-color: rgba(56, 142, 60, 0.15); border-color: rgba(56, 142, 60, 0.4); color: #81C784; }
        &.color-lead { background-color: rgba(84, 110, 122, 0.15); border-color: rgba(84, 110, 122, 0.4); color: #90A4AE; }
        &.color-success { background-color: rgba(46, 125, 50, 0.15); border-color: rgba(46, 125, 50, 0.4); color: #81C784; }
        &.color-danger { background-color: rgba(198, 40, 40, 0.15); border-color: rgba(198, 40, 40, 0.4); color: #EF5350; }
        &.color-default { background-color: #2d2d2d; border-color: rgba(255, 255, 255, 0.12); color: #808080; }
      }

      .detail-card {
        background-color: #2a2a2a;
        border-color: rgba(255, 255, 255, 0.08);
      }

      .card-header {
        background-color: #2d2d2d;
        border-bottom-color: rgba(255, 255, 255, 0.08);
        color: #f5f5f5;

        .material-icons { color: #808080; }
      }

      .pinned-badge {
        color: #FFB74D;
        .material-icons { color: #FFB74D; }
      }

      .detail-item {
        .label { color: #808080; }
        .value { color: #f5f5f5; }
      }

      .status-badge {
        background-color: #2d2d2d;
        color: #b0b0b0;

        &.completed {
          background-color: rgba(56, 142, 60, 0.15);
          color: #81C784;
        }
      }

      .direction-badge {
        background-color: rgba(57, 73, 171, 0.15);
        color: #9FA8DA;

        &.inbound {
          background-color: rgba(123, 31, 162, 0.15);
          color: #CE93D8;
        }
      }

      .step-badge {
        background-color: rgba(0, 131, 143, 0.15);
        color: #4DD0E1;
      }

      .pipeline-name { color: #808080; }
      .content-section .label { color: #808080; }

      .content-box {
        background-color: #1a1a1a;
        border-color: rgba(255, 255, 255, 0.08);
        color: #f5f5f5;
      }

      .audio-section {
        .label { color: #808080; }
        audio { filter: invert(1) hue-rotate(180deg); }
        .loading-audio { background: #2d2d2d; }
        .error-audio { background: rgba(198, 40, 40, 0.15); color: #EF5350; }
      }

      .no-details {
        color: #808080;
        .material-icons { color: #606060; }
      }
    }
  `]
})
export class ActivityLogDetailModalComponent {
  recordingUrl: string | null = null;
  loadingRecording = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<ActivityLogDetailModalComponent>,
    private http: HttpClient
  ) {
    if (data.call?.recording?.s3_key) {
      this.loadRecordingUrl(data.call.recording.s3_key);
    }
  }

  close() {
    this.dialogRef.close();
  }

  // Check if direction is received/inbound
  isReceived(direction: string): boolean {
    return direction === 'received' || direction === 'inbound';
  }

  // Get message status - "Pending" only for sent messages
  getMessageStatus(message: any): string {
    if (this.isReceived(message.direction)) {
      return 'Received';
    }
    if (message.delivered) {
      return 'Delivered';
    }
    if (message.isError) {
      return 'Failed';
    }
    return 'Pending';
  }

  getEventConfig(eventType: string): { icon: string; colorClass: string; label: string } {
    const config: Record<string, { icon: string; colorClass: string; label: string }> = {
      activity_created: { icon: 'add_task', colorClass: 'color-activity', label: 'Activity Created' },
      activity_completed: { icon: 'task_alt', colorClass: 'color-activity-done', label: 'Activity Completed' },
      activity_updated: { icon: 'edit', colorClass: 'color-activity', label: 'Activity Updated' },
      activity_deleted: { icon: 'delete_outline', colorClass: 'color-danger', label: 'Activity Deleted' },
      sms_sent: { icon: 'sms', colorClass: 'color-message-out', label: 'SMS Sent' },
      sms_received: { icon: 'sms', colorClass: 'color-message-in', label: 'SMS Received' },
      email_sent: { icon: 'mail_outline', colorClass: 'color-message-out', label: 'Email Sent' },
      email_received: { icon: 'mark_email_unread', colorClass: 'color-message-in', label: 'Email Received' },
      lead_incoming_message: { icon: 'move_to_inbox', colorClass: 'color-message-in', label: 'Message Received' },
      lead_outgoing_message: { icon: 'send', colorClass: 'color-message-out', label: 'Message Sent' },
      note_created: { icon: 'sticky_note_2', colorClass: 'color-note', label: 'Note Added' },
      note_updated: { icon: 'edit_note', colorClass: 'color-note', label: 'Note Updated' },
      lead_note_added: { icon: 'sticky_note_2', colorClass: 'color-note', label: 'Note Added' },
      lead_note_updated: { icon: 'edit_note', colorClass: 'color-note', label: 'Note Updated' },
      step_changed: { icon: 'swap_horiz', colorClass: 'color-step', label: 'Step Changed' },
      lead_step_changed: { icon: 'swap_horiz', colorClass: 'color-step', label: 'Step Changed' },
      appointment_scheduled: { icon: 'event', colorClass: 'color-appointment', label: 'Appointment Scheduled' },
      appointment_completed: { icon: 'event_available', colorClass: 'color-appointment-done', label: 'Appointment Completed' },
      appointment_cancelled: { icon: 'event_busy', colorClass: 'color-danger', label: 'Appointment Cancelled' },
      appointment_rescheduled: { icon: 'update', colorClass: 'color-appointment', label: 'Appointment Rescheduled' },
      call_incoming_completed: { icon: 'phone_callback', colorClass: 'color-call', label: 'Incoming Call' },
      call_outgoing_completed: { icon: 'phone_forwarded', colorClass: 'color-call', label: 'Outgoing Call' },
      call_missed_inbound: { icon: 'phone_missed', colorClass: 'color-danger', label: 'Missed Call' },
      call_missed_outbound: { icon: 'phone_disabled', colorClass: 'color-danger', label: 'No Answer' },
      call_started: { icon: 'phone_in_talk', colorClass: 'color-call', label: 'Call Started' },
      call_completed: { icon: 'phone', colorClass: 'color-call', label: 'Call Completed' },
      call_failed: { icon: 'phone_locked', colorClass: 'color-danger', label: 'Call Failed' },
      lead_created: { icon: 'person_add', colorClass: 'color-lead', label: 'Lead Created' },
      lead_updated: { icon: 'person', colorClass: 'color-lead', label: 'Lead Updated' },
      lead_won: { icon: 'emoji_events', colorClass: 'color-success', label: 'Lead Won' },
      lead_lost: { icon: 'person_off', colorClass: 'color-danger', label: 'Lead Lost' },
    };

    return config[eventType] || { 
      icon: 'radio_button_checked', 
      colorClass: 'color-default', 
      label: eventType.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') 
    };
  }

  formatDuration(seconds: number | null): string {
    if (!seconds) return '0s';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins ? `${mins}m ${secs}s` : `${secs}s`;
  }

  hasMetadata(metadata: any): boolean {
    return metadata && Object.keys(metadata).length > 0;
  }

  getMetadataItems(metadata: any): { key: string; label: string; value: string; fullWidth: boolean }[] {
    if (!metadata) return [];
    
    const items: { key: string; label: string; value: string; fullWidth: boolean }[] = [];
    
    for (const key of Object.keys(metadata)) {
      const value = metadata[key];
      if (value === null || value === undefined || value === '') continue;
      
      const label = key
        .replace(/_/g, ' ')
        .split(' ')
        .map(w => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');
      
      const stringValue = typeof value === 'object' ? JSON.stringify(value) : String(value);
      const fullWidth = stringValue.length > 30;
      
      items.push({ key, label, value: stringValue, fullWidth });
    }
    
    return items;
  }

  async loadRecordingUrl(s3Key: string): Promise<void> {
    this.loadingRecording = true;
    try {
      const response = await firstValueFrom(
        this.http.get<{ signUrl: string }>(`${environment.apiUrl}/aws/signUrlNoPublic`, {
          params: { key: s3Key, expSeconds: '3600' }
        })
      );
      this.recordingUrl = response?.signUrl || null;
    } catch (error) {
      console.error('Failed to load recording URL:', error);
    } finally {
      this.loadingRecording = false;
    }
  }
}